import { useState } from "react";
import Layout from "../../components/Layout";
import { useGetStudentNotAssignedClass } from "../../hooks/query/useGetAllStudentRegister";

// ── Types ────────────────────────────────────────────────────────────────────
interface StudentItem {
    id: number;
    email: string;
    fullName: string;
    gender: string;
    dateOfBirth: string;
    phoneNumber: string;
    address: string;
    avatarUrl: string;
    status: string;
    userType: string;
    userTypeId: number;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    student: {
        id: number;
        schoolGrade: number;
        startDate: string;
        description: string;
    };
}

interface Meta {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
}

// ── Sub-components ───────────────────────────────────────────────────────────
function StatusBadge() {
    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
            Chưa xếp lớp
        </span>
    );
}

function TableSkeleton({ rows = 10 }: { rows?: number }) {
    return (
        <>
            {Array.from({ length: rows }).map((_, i) => (
                <tr key={i} className="animate-pulse border-b border-gray-100">
                    {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                            <div className="h-4 bg-gray-200 rounded w-3/4" />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

function Pagination({
    page,
    totalPages,
    onPageChange,
}: {
    page: number;
    totalPages: number;
    onPageChange: (p: number) => void;
}) {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const delta = 2;
        const range: number[] = [];
        for (
            let i = Math.max(1, page - delta);
            i <= Math.min(totalPages, page + delta);
            i++
        ) {
            range.push(i);
        }
        return range;
    };

    return (
        <div className="flex items-center justify-end gap-1 mt-4">
            <button
                disabled={page === 1}
                onClick={() => onPageChange(1)}
                className="px-2.5 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition"
            >
                «
            </button>
            <button
                disabled={page === 1}
                onClick={() => onPageChange(page - 1)}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition"
            >
                ‹
            </button>
            {getPageNumbers().map((p) => (
                <button
                    key={p}
                    onClick={() => onPageChange(p)}
                    className={`px-3 py-1.5 text-sm rounded border transition ${
                        p === page
                            ? "bg-blue-600 text-white border-blue-600"
                            : "border-gray-300 hover:bg-gray-50"
                    }`}
                >
                    {p}
                </button>
            ))}
            <button
                disabled={page === totalPages}
                onClick={() => onPageChange(page + 1)}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition"
            >
                ›
            </button>
            <button
                disabled={page === totalPages}
                onClick={() => onPageChange(totalPages)}
                className="px-2.5 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition"
            >
                »
            </button>
        </div>
    );
}

// ── Main ─────────────────────────────────────────────────────────────────────
const LIMIT = 10;

export default function ListStudentRegister() {
    const [page, setPage] = useState(1);

    const { data, isLoading, isError } = useGetStudentNotAssignedClass({ page, limit: LIMIT });

    const registrations: StudentItem[] = data?.data?.items ?? [];
    const meta: Meta | undefined = data?.data?.meta;
    const totalItems = meta?.totalItems ?? 0;
    const totalPages = meta?.totalPages ?? 0;

    return (
        <Layout>
            <div className="p-6 max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">
                        Danh sách học sinh đăng ký
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Học sinh đã đăng ký môn học nhưng chưa được phân lớp
                    </p>
                </div>

                {/* Tổng số */}
                <div className="flex items-center justify-end mb-4">
                    <span className="text-sm text-gray-500">
                        Tổng: <strong className="text-gray-800">{totalItems}</strong> học sinh
                    </span>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm bg-white">
                    <table className="min-w-full text-sm text-gray-700">
                        <thead>
                            <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                                <th className="px-4 py-3 text-left font-semibold">#</th>
                                <th className="px-4 py-3 text-left font-semibold">Họ và tên</th>
                                <th className="px-4 py-3 text-left font-semibold">Email</th>
                                <th className="px-4 py-3 text-left font-semibold">Số điện thoại</th>
                                <th className="px-4 py-3 text-left font-semibold">Khối</th>
                                <th className="px-4 py-3 text-left font-semibold">Ngày bắt đầu</th>
                                <th className="px-4 py-3 text-left font-semibold">Trạng thái</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <TableSkeleton rows={LIMIT} />
                            ) : isError ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-10 text-center text-red-500">
                                        Đã xảy ra lỗi khi tải dữ liệu. Vui lòng thử lại.
                                    </td>
                                </tr>
                            ) : registrations.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                                        Không có học sinh nào đang chờ xếp lớp
                                    </td>
                                </tr>
                            ) : (
                                registrations.map((student, idx) => (
                                    <tr
                                        key={student.id}
                                        className="border-b border-gray-100 hover:bg-blue-50/40 transition-colors"
                                    >
                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                                            {(page - 1) * LIMIT + idx + 1}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-gray-900">
                                            {student.fullName}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">
                                            {student.email}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">
                                            {student.phoneNumber || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">
                                            Khối {student.student?.schoolGrade ?? "—"}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                                            {student.student?.startDate
                                                ? new Date(student.student.startDate).toLocaleDateString("vi-VN")
                                                : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
        </Layout>
    );
}